import React, { useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { Toast, ToastContainer } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import LiveLister from "../../components/Lister/LiveLister";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import Popup from "../../components/Popup/Popup";
import { useSession } from "../../contexts/SessionContext";
import { CohortIntensity } from "../../models/CohortIntensity";
import { LanguageEnum } from "../../models/Language";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { capitalizeWord, completeNavigate } from "../../services/UtilityService";
import { fetchCohortsLeaders, fetchCohortsManagers, generateRemoteUrl, getIntensityColor } from "./Cohorts.function";
import "./Cohorts.scss";
import CohortGeneralForm from "./components/CohortGeneralForm/CohortGeneralForm";

export const Cohorts = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [programManagerFilter, setProgramManagerFilter] = useState<any[]>([]);
  const [cohortLeaderFilter, setCohortLeaderFilter] = useState<any[]>([]);
  const [selectedProgramManagerFilter, setSelectedProgramManagerFilter] = useState<any[]>([]);
  const [selectedCohortLeaderFilter, setSelectedCohortLeaderFilter] = useState<any[]>([]);
  const [selectedIntensityFilter, setSelectedIntensityFilter] = useState<any[]>([]);
  const [selectedLanguageFilter, setSelectedLanguageFilter] = useState<any[]>([]);
  const [loadedLeaders, setLoadedLeaders] = useState(false);
  const [loadedProgramManagers, setLoadedProgramManagers] = useState(false);
  const [detailedSearch, setDetailedSearch] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showCreateCohortToast, setShowCreateCohortToast] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeFilterCount = [
    selectedCohortLeaderFilter.length > 0,
    selectedProgramManagerFilter.length > 0,
    selectedIntensityFilter.length > 0,
    selectedLanguageFilter.length > 0,
  ].filter(Boolean).length;

  const _clearAllFilters = () => {
    setSelectedCohortLeaderFilter([]);
    setSelectedProgramManagerFilter([]);
    setSelectedIntensityFilter([]);
    setSelectedLanguageFilter([]);
  };

  const hasActiveFilters =
    selectedCohortLeaderFilter.length > 0 ||
    selectedProgramManagerFilter.length > 0 ||
    selectedIntensityFilter.length > 0 ||
    selectedLanguageFilter.length > 0;

  const columns = [
    { header: translate(language, "PAGES.COHORTS.ID"), key: "id", sortable: true },
    { header: translate(language, "PAGES.COHORTS.NAME"), key: "name", sortable: true },
    ...(detailedSearch
      ? [
          {
            header: translate(language, "PAGES.COHORTS.DESCRIPTION"),
            key: "description",
          },
          {
            header: translate(language, "PAGES.COHORTS.CAMPUS.TITLE"),
            key: "campus",
            render: (item: any) => (
              <React.Fragment>
                {item.campus.join(", ") || translate(language, "PAGES.COHORTS.CAMPUS.NO_CAMPUS_NAME")}
              </React.Fragment>
            ),
          },
        ]
      : []),
    { header: translate(language, "PAGES.COHORTS.COHORT_LEADER"), key: "cohort_leader" },
    { header: translate(language, "PAGES.COHORTS.PROGRAM_MANAGER"), key: "program_manager" },
    {
      header: translate(language, "PAGES.COHORTS.SIZE"),
      key: "users_count",
      render: (item: any) => (
        <span>
          {item.users_count} <span className="dst-icon-users-01" />
        </span>
      ),
    },
    {
      header: translate(language, "PAGES.COHORTS.START_DATE"),
      key: "dt_start",
      sortable: true,
      render: (item: any) => (
        <span title={formatDateHumanly(language, item.start_date, true)}>
          {formatDateHumanly(language, item.start_date)}
        </span>
      ),
    },
    {
      header: translate(language, "PAGES.COHORTS.END_DATE"),
      key: "dt_end",
      sortable: true,
      render: (item: any) => (
        <span title={formatDateHumanly(language, item.end_date, true)}>
          {formatDateHumanly(language, item.end_date)}
        </span>
      ),
    },
    {
      header: translate(language, "PAGES.COHORTS.END_DATE_PLANNED"),
      key: "dt_end_planned",
      sortable: true,
      render: (item: any) =>
        item.end_date_planned ? (
          <span title={formatDateHumanly(language, item.end_date_planned, true)}>
            {formatDateHumanly(language, item.end_date_planned)}
          </span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      header: translate(language, "PAGES.COHORTS.INTENSITY"),
      key: "intensity",
      render: (item: any) => (
        <Badge pill {...getIntensityColor(item.intensity)}>
          {item.intensity}
        </Badge>
      ),
    },
  ];

  useEffect(
    () => fetchCohortsLeaders(language, setCohortLeaderFilter, false, () => setLoadedLeaders(true)),
    [language, loadedLeaders]
  );
  useEffect(
    () => fetchCohortsManagers(language, setProgramManagerFilter, false, () => setLoadedProgramManagers(true)),
    [language, loadedProgramManagers]
  );

  const ActiveFilterChips = () => (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      {selectedCohortLeaderFilter.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle clickable"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.COHORTS.FILTERS.LEADERS")} ({selectedCohortLeaderFilter.length})
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedCohortLeaderFilter([]);
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedProgramManagerFilter.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle clickable"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.COHORTS.FILTERS.PROGRAM_MANAGERS")} ({selectedProgramManagerFilter.length})
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedProgramManagerFilter([]);
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedIntensityFilter.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle clickable"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.COHORTS.FILTERS.INTENSITY")} ({selectedIntensityFilter.length})
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedIntensityFilter([]);
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedLanguageFilter.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle clickable"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.COHORTS.FILTERS.LANGUAGE")} ({selectedLanguageFilter.length})
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedLanguageFilter([]);
            }}
          >
            ✕
          </span>
        </span>
      )}
      {hasActiveFilters && (
        <span className="clickable dark-gray-font text-decoration-underline xs-regular" onClick={_clearAllFilters}>
          {translate(language, "PAGES.COHORTS.FILTERS.CLEAR_FILTERS")}
        </span>
      )}
    </div>
  );

  return (
    <div className="cohorts-container d-flex flex-column">
      <div className="d-flex align-items-center gap-2 p-3 white-background border-bottom flex-wrap">
        <h2 className="mb-0 me-1">{translate(language, "PAGES.COHORTS.TITLE")}</h2>
        <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        <DstButton
          btnClass="d-flex align-items-center"
          btnImageBefore={
            <Form.Check
              type="switch"
              className="check-box s-bold me-2"
              id="detailed-search-switch"
              checked={detailedSearch}
              onChange={() => setDetailedSearch(!detailedSearch)}
              disabled={!loadedProgramManagers}
            />
          }
          value={translate(language, "PAGES.COHORTS.ENABLE_DETAILED_SEARCH")}
          variant="light"
          mock={!loadedProgramManagers}
          clickFunction={() => setDetailedSearch(!detailedSearch)}
        />

        <div className="position-relative">
          <DstButton
            variant={activeFilterCount > 0 ? "primary" : "light"}
            btnImageBefore={<span className="dst-icon-filter-lines me-2" />}
            value={translate(language, "PAGES.COHORTS.FILTERS.TITLE")}
            clickFunction={() => setDrawerOpen(true)}
          />
          {activeFilterCount > 0 && (
            <span
              className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              style={{ fontSize: 10 }}
            >
              {activeFilterCount}
            </span>
          )}
        </div>
        <div className="ms-auto">
          <DstButton
            btnImageBefore={<span className="dst-icon-plus-circle me-2" />}
            value={translate(language, "PAGES.COHORTS.CREATE_COHORT.BUTTON.ADD_COHORT")}
            variant="primary"
            clickFunction={() => setShowCreatePopup(true)}
          />
        </div>
      </div>
      <div className="p-3 flex-grow-1 overflow-auto">
        <ActiveFilterChips />
        <LiveLister
          key={reloadKey}
          headerClass="text-nowrap"
          remote={generateRemoteUrl(
            searchQuery,
            selectedCohortLeaderFilter,
            selectedProgramManagerFilter,
            selectedIntensityFilter,
            selectedLanguageFilter,
            detailedSearch
          )}
          remoteKey="cohorts"
          remoteTransform={(cohort: any) => ({
            id: cohort.id,
            name: cohort.name,
            description: cohort.description,
            campus: cohort.campus || [],
            cohort_leader: cohort.cohort_leader.full_name,
            program_manager: cohort.program_manager?.full_name || "N/A",
            start_date: cohort.dt_start,
            end_date: cohort.dt_end,
            end_date_planned: cohort.dt_end_planned || null,
            intensity: cohort.intensity,
            users_count: cohort.users_count,
          })}
          columns={columns}
          additionalButton={(item: any) => (
            <span
              className="clickable xs-regular"
              onClick={(event) => completeNavigate(event, navigate, `/cohort/${item.id}`)}
            >
              <span className="dst-icon-arrow-narrow-right" style={{ fontSize: 15 }} />
            </span>
          )}
          showHeader
          pagination
          paginationAlwaysShowFooter
          itemsPerPage={20}
        />
      </div>

      {drawerOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 overlay-backdrop"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div
        className={`drawer-right position-fixed top-0 end-0 h-100 white-background d-flex flex-column ${
          drawerOpen ? "is-open" : ""
        }`}
      >
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <span className="s-bold h5 mb-0">{translate(language, "PAGES.COHORTS.FILTERS.TITLE")}</span>
          <DstButton
            variant="light"
            value=""
            btnImageBefore={<span className="dst-icon-x-close" />}
            clickFunction={() => setDrawerOpen(false)}
          />
        </div>
        <div className="p-3 d-flex flex-column gap-3 overflow-auto flex-grow-1">
          <div className="d-flex flex-column gap-2">
            <MultiFilterDropdown
              tags={cohortLeaderFilter.map((leader: any) => ({
                display: leader.full_name,
                value: leader.id,
              }))}
              onSelectTags={(leader) => setSelectedCohortLeaderFilter(leader)}
              defaultValue={selectedCohortLeaderFilter}
              language={language}
              inputTitle={translate(language, "PAGES.COHORTS.FILTERS.LEADERS")}
              inputLabel={translate(language, "PAGES.COHORTS.FILTERS.INPUT_LABEL")}
              inputPlaceholder={translate(language, "PAGES.COHORTS.FILTERS.INPUT_PLACEHOLDER")}
              showSearchBar
              enableSelectAll
              considerEmptyOnAllSelected
              mock={!loadedLeaders}
              buttonWidth="280px"
            />

            <MultiFilterDropdown
              tags={programManagerFilter.map((manager: any) => ({
                display: manager.full_name,
                value: manager.id,
              }))}
              onSelectTags={(manager) => setSelectedProgramManagerFilter(manager)}
              defaultValue={selectedProgramManagerFilter}
              language={language}
              inputTitle={translate(language, "PAGES.COHORTS.FILTERS.PROGRAM_MANAGERS")}
              inputLabel={translate(language, "PAGES.COHORTS.FILTERS.INPUT_LABEL")}
              inputPlaceholder={translate(language, "PAGES.COHORTS.FILTERS.INPUT_PLACEHOLDER")}
              showSearchBar
              enableSelectAll
              considerEmptyOnAllSelected
              mock={!loadedProgramManagers}
              buttonWidth="280px"
            />

            <MultiFilterDropdown
              tags={Object.values(CohortIntensity).map((intensity: CohortIntensity | string) => ({
                value: intensity,
                display: capitalizeWord(intensity),
              }))}
              onSelectTags={(intensity) => setSelectedIntensityFilter(intensity)}
              defaultValue={selectedIntensityFilter}
              language={language}
              inputTitle={translate(language, "PAGES.COHORTS.FILTERS.INTENSITY")}
              inputLabel={translate(language, "PAGES.COHORTS.FILTERS.INPUT_LABEL")}
              inputPlaceholder={translate(language, "PAGES.COHORTS.FILTERS.INPUT_PLACEHOLDER")}
              showSearchBar
              enableSelectAll
              considerEmptyOnAllSelected
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              tags={Object.values(LanguageEnum).map((lang) => ({
                value: lang,
                display: lang,
              }))}
              onSelectTags={(lang) => setSelectedLanguageFilter(lang)}
              defaultValue={selectedLanguageFilter}
              language={language}
              inputTitle={translate(language, "PAGES.COHORTS.FILTERS.LANGUAGE")}
              inputLabel={translate(language, "PAGES.COHORTS.FILTERS.INPUT_LABEL")}
              inputPlaceholder={translate(language, "PAGES.COHORTS.FILTERS.INPUT_PLACEHOLDER")}
              buttonWidth="280px"
            />
          </div>
        </div>
        {hasActiveFilters && (
          <div className="p-3 border-top">
            <DstButton
              variant="secondary"
              btnClass="w-100"
              value={translate(language, "PAGES.COHORTS.FILTERS.CLEAR_FILTERS")}
              clickFunction={_clearAllFilters}
            />
          </div>
        )}
      </div>
      <Popup
        show={showCreatePopup}
        onClose={() => setShowCreatePopup(false)}
        modalTitle={translate(language, "PAGES.COHORTS.CREATE_COHORT.TITLE")}
      >
        <CohortGeneralForm
          onSuccess={() => {
            setShowCreatePopup(false);
            setReloadKey((prev) => prev + 1);
            setShowCreateCohortToast(true);
          }}
          onCancel={() => setShowCreatePopup(false)}
        />
      </Popup>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg="success"
          show={showCreateCohortToast}
          onClose={() => setShowCreateCohortToast(false)}
          delay={2000}
          autohide
        >
          <Toast.Body className="text-white">{translate(language, "PAGES.COHORTS.SUCCESS.CREATION")}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Cohorts;
